import { useEffect } from 'react';
import Animated, {
    cancelAnimation,
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withRepeat,
    withTiming,
} from 'react-native-reanimated';
import { SafeAreaView as RNSACSafeAreaView } from 'react-native-safe-area-context';
import { type ViewProps, XStack } from 'tamagui';

import { useThemeColors } from '../../themes/SmoothieColorTokens';

// The original component had multiple sizes, but for now we are just going with one size
export enum SpinnerSize {
    SMALL = 24,
}

interface SpinnerProps extends ViewProps {
    size?: SpinnerSize;
}

export const Spinner = ({
    size = SpinnerSize.SMALL,
    ...props
}: SpinnerProps) => {
    const { getColor } = useThemeColors();
    const borderColor = getColor('$gray200');
    const borderTopColor = getColor('$green700');

    const rotation = useSharedValue(0);
    const animatedStyles = useAnimatedStyle(
        () => ({
            transform: [
                {
                    rotateZ: `${rotation.value}deg`,
                },
            ],
        }),
        [rotation],
    );

    useEffect(() => {
        rotation.value = withRepeat(
            withTiming(350, {
                duration: 600,
                easing: Easing.inOut(Easing.sin),
            }),
            withTiming(0, {
                duration: 700,
                easing: Easing.inOut(Easing.sin),
            }),
        );
        return () => {
            cancelAnimation(rotation);
        };
    }, []);
    return (
        <XStack
            justifyContent="center"
            alignItems="center"
            width="100%"
            {...props}
        >
            <Animated.View
                style={[
                    {
                        height: size,
                        width: size,
                        borderRadius: 100,
                        borderWidth: 2,
                        borderTopWidth: 3,
                        borderColor,
                        borderTopColor,
                    },
                    animatedStyles,
                ]}
            />
        </XStack>
    );
};

export const FullScreenSpinner = () => {
    return (
        <RNSACSafeAreaView
            style={{
                height: '100%',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: 0,
            }}
        >
            <Spinner />
        </RNSACSafeAreaView>
    );
};
